/**
 * The misspelled words to underline on the canvas, keyed by shape id.
 *
 * The Spell Check panel finds the words and the text frames draw them, and the two do not share a
 * parent: the panel lives in the left panel and the frames inside `ShapeManager`. A module-level
 * store, like the shape-text frame registry, lets the panel publish and each frame read just its
 * own entry. An unchanged frame keeps the same array reference across a publish, so typing in one
 * frame redraws only that frame.
 *
 * The panel publishes while it is open with Show underlines on, and clears when it closes.
 */
import type { SpellUnderlineRange } from '@/lib/spell-check/spell-check-state'

const NO_RANGES: readonly SpellUnderlineRange[] = []

let rangesByShape = new Map<string, readonly SpellUnderlineRange[]>()
const listeners = new Set<() => void>()

function sameRanges(a: readonly SpellUnderlineRange[], b: readonly SpellUnderlineRange[]) {
  return (
    a.length === b.length &&
    a.every((range, i) => range.start === b[i].start && range.end === b[i].end)
  )
}

function notify(): void {
  listeners.forEach((listener) => listener())
}

/** Replaces what is underlined. Subscribers are told only when something actually changed. */
export function setSpellUnderlines(
  next: ReadonlyMap<string, readonly SpellUnderlineRange[]>
): void {
  const merged = new Map<string, readonly SpellUnderlineRange[]>()
  let changed = next.size !== rangesByShape.size

  next.forEach((ranges, shapeId) => {
    const previous = rangesByShape.get(shapeId)

    if (previous && sameRanges(previous, ranges)) {
      merged.set(shapeId, previous)
    } else {
      merged.set(shapeId, ranges)
      changed = true
    }
  })

  if (!changed) return

  rangesByShape = merged
  notify()
}

export function clearSpellUnderlines(): void {
  if (rangesByShape.size === 0) return

  rangesByShape = new Map()
  notify()
}

/** The ranges to underline in one frame. The same array while they are unchanged. */
export function getSpellUnderlines(shapeId: string): readonly SpellUnderlineRange[] {
  return rangesByShape.get(shapeId) ?? NO_RANGES
}

export function subscribeToSpellUnderlines(listener: () => void): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}
